import { supabase } from '@/lib/supabase'

export const REWARDS_SETTINGS_KEYS = {
  enabled: 'rewards.enabled',
  syncEnabled: 'rewards.sync.enabled',
  trialEnabled: 'rewards.trial.enabled',
  trialMinVolumeUsdt: 'rewards.trial.min_volume_usdt',
  trialWindowDays: 'rewards.trial.window_days',
  trialPeriodDays: 'rewards.trial.period_days',
  multiplierX: 'rewards.multiplier_x',
  usdtBrlRate: 'rewards.usdt_brl_rate',
  homeEnabled: 'rewards.home.enabled',
} as const

export type RewardsAppSettings = {
  enabled: boolean
  syncEnabled: boolean
  trialEnabled: boolean
  trialMinVolumeUsdt: number
  trialWindowDays: number
  trialPeriodDays: number
  multiplierX: number
  usdtBrlRate: number
  homeEnabled: boolean
}

function toBool(v?: string | null): boolean {
  return String(v || '').trim().toLowerCase() === 'true'
}
function toNumber(v: string | null | undefined, fallback: number): number {
  const n = Number(String(v || '').trim())
  return Number.isFinite(n) ? n : fallback
}

export async function getRewardsAppSettings(): Promise<RewardsAppSettings> {
  const keys = Object.values(REWARDS_SETTINGS_KEYS)
  const { data, error } = await supabase.from('app_settings').select('key,value').in('key', keys)
  if (error) throw error
  const map = new Map<string, string>((data || []).map((r: any) => [String(r.key), String(r.value || '')]))

  return {
    enabled: toBool(map.get(REWARDS_SETTINGS_KEYS.enabled)),
    syncEnabled: toBool(map.get(REWARDS_SETTINGS_KEYS.syncEnabled)),
    trialEnabled: toBool(map.get(REWARDS_SETTINGS_KEYS.trialEnabled)),
    trialMinVolumeUsdt: toNumber(map.get(REWARDS_SETTINGS_KEYS.trialMinVolumeUsdt), 50000),
    trialWindowDays: toNumber(map.get(REWARDS_SETTINGS_KEYS.trialWindowDays), 7),
    trialPeriodDays: toNumber(map.get(REWARDS_SETTINGS_KEYS.trialPeriodDays), 30),
    multiplierX: toNumber(map.get(REWARDS_SETTINGS_KEYS.multiplierX), 3),
    usdtBrlRate: toNumber(map.get(REWARDS_SETTINGS_KEYS.usdtBrlRate), 0),
    homeEnabled: toBool(map.get(REWARDS_SETTINGS_KEYS.homeEnabled)),
  }
}

export async function setRewardsAppSettings(next: Partial<RewardsAppSettings>): Promise<void> {
  const rows: Array<{ key: string; value: string }> = []

  if (typeof next.enabled === 'boolean') rows.push({ key: REWARDS_SETTINGS_KEYS.enabled, value: next.enabled ? 'true' : 'false' })
  if (typeof next.syncEnabled === 'boolean')
    rows.push({ key: REWARDS_SETTINGS_KEYS.syncEnabled, value: next.syncEnabled ? 'true' : 'false' })
  if (typeof next.trialEnabled === 'boolean')
    rows.push({ key: REWARDS_SETTINGS_KEYS.trialEnabled, value: next.trialEnabled ? 'true' : 'false' })
  if (typeof next.homeEnabled === 'boolean')
    rows.push({ key: REWARDS_SETTINGS_KEYS.homeEnabled, value: next.homeEnabled ? 'true' : 'false' })

  if (typeof next.trialMinVolumeUsdt === 'number')
    rows.push({ key: REWARDS_SETTINGS_KEYS.trialMinVolumeUsdt, value: String(next.trialMinVolumeUsdt) })
  if (typeof next.trialWindowDays === 'number')
    rows.push({ key: REWARDS_SETTINGS_KEYS.trialWindowDays, value: String(next.trialWindowDays) })
  if (typeof next.trialPeriodDays === 'number')
    rows.push({ key: REWARDS_SETTINGS_KEYS.trialPeriodDays, value: String(next.trialPeriodDays) })
  if (typeof next.multiplierX === 'number')
    rows.push({ key: REWARDS_SETTINGS_KEYS.multiplierX, value: String(next.multiplierX) })
  if (typeof next.usdtBrlRate === 'number')
    rows.push({ key: REWARDS_SETTINGS_KEYS.usdtBrlRate, value: String(next.usdtBrlRate) })

  if (rows.length === 0) return

  const { error } = await supabase.from('app_settings').upsert(rows, { onConflict: 'key' })
  if (error) throw error
}

export type RewardsExchangeSetting = {
  exchange: 'bingx' | 'bybit' | 'bitget'
  enabled: boolean
  commission_bps: number
  maker_fee_rate: number
  taker_fee_rate: number
  signup_url: string | null
  notes: string | null
  updated_at: string
}

export async function listRewardsExchangeSettings(): Promise<RewardsExchangeSetting[]> {
  const { data, error } = await supabase
    .from('rewards_exchange_settings')
    .select('exchange,enabled,commission_bps,maker_fee_rate,taker_fee_rate,signup_url,notes,updated_at')
    .order('exchange', { ascending: true })
  if (error) throw error
  return ((data || []) as any[]).map((r) => ({
    exchange: r.exchange,
    enabled: !!r.enabled,
    commission_bps: Number(r.commission_bps || 0),
    maker_fee_rate: Number(r.maker_fee_rate || 0),
    taker_fee_rate: Number(r.taker_fee_rate || 0),
    signup_url: r.signup_url || null,
    notes: r.notes || null,
    updated_at: r.updated_at,
  }))
}

export async function updateRewardsExchangeSetting(
  exchange: RewardsExchangeSetting['exchange'],
  patch: Partial<Pick<RewardsExchangeSetting, 'enabled' | 'commission_bps' | 'maker_fee_rate' | 'taker_fee_rate' | 'signup_url' | 'notes'>>,
): Promise<void> {
  const { error } = await supabase.from('rewards_exchange_settings').update(patch as any).eq('exchange', exchange)
  if (error) throw error
}
